import { create } from 'zustand';
import { useEffect } from 'react';

export enum FocusModePriority {
    PAGE = 1,
    SLOT = 2,
    COMPONENT = 3,
}

interface FocusModeRegistration {
    id: string;
    priority: FocusModePriority;
    isFocusModeDesktop: boolean;
    isFocusModeMobile: boolean;
}

interface FocusModeStore {
    registrations: Record<string, FocusModeRegistration>;
    registerFocusMode: (
        id: string,
        priority: FocusModePriority,
        isFocusModeDesktop: boolean,
        isFocusModeMobile: boolean
    ) => void;
    unregisterFocusMode: (id: string) => void;
    getFocusMode: () => { isFocusModeDesktop: boolean; isFocusModeMobile: boolean };
}

export const useFocusModeStore = create<FocusModeStore>((set, get) => ({
    registrations: {},
    registerFocusMode: (id, priority, isFocusModeDesktop, isFocusModeMobile) => {
        set((state) => ({
            registrations: {
                ...state.registrations,
                [id]: { id, priority, isFocusModeDesktop, isFocusModeMobile },
            },
        }));
    },
    unregisterFocusMode: (id) => {
        set((state) => {
            const next = { ...state.registrations };
            delete next[id];
            return { registrations: next };
        });
    },
    getFocusMode: () => {
        const regs = Object.values(get().registrations);
        if (regs.length === 0) {
            return { isFocusModeDesktop: false, isFocusModeMobile: false };
        }
        // Sort descending by priority, so highest priority is first
        regs.sort((a, b) => b.priority - a.priority);
        const highestReg = regs[0];
        return {
            isFocusModeDesktop: highestReg.isFocusModeDesktop,
            isFocusModeMobile: highestReg.isFocusModeMobile,
        };
    },
}));

export const useRegisterFocusMode = (
    id: string,
    priority: FocusModePriority,
    isFocusModeDesktop: boolean,
    isFocusModeMobile: boolean,
    enabled: boolean = true
) => {
    const register = useFocusModeStore((s) => s.registerFocusMode);
    const unregister = useFocusModeStore((s) => s.unregisterFocusMode);

    useEffect(() => {
        if (enabled) {
            register(id, priority, isFocusModeDesktop, isFocusModeMobile);
        } else {
            unregister(id);
        }
        return () => {
            unregister(id);
        };
    }, [id, priority, isFocusModeDesktop, isFocusModeMobile, enabled, register, unregister]);
};

export const useComputedFocusMode = () => {
    const getFocusMode = useFocusModeStore((s) => s.getFocusMode);
    useFocusModeStore((s) => s.registrations); // subscribe to registrations to trigger re-renders
    return getFocusMode();
};
